import { createColumnHelper } from '@tanstack/react-table';
import { useContext } from 'react';
import { IconButton } from 'src/components/Buttons/IconButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { ColumnPicker } from 'src/components/TableV2/components/ColumnPicker';
import {
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext/ModalContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getSalesGroupModalConfigWithDeepLink } from 'src/modals/SalesGroup';
import { OpenLinkIcon } from 'src/svgs/OpenLinkIcon';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { createCopyColumn } from 'src/tables/TableV2/factories/copyColumnFactory';
import { createDateColumn } from 'src/tables/TableV2/factories/dateColumnFactory';
import { createMoneyColumn } from 'src/tables/TableV2/factories/moneyColumnFactory';
import { createTextColumn } from 'src/tables/TableV2/factories/textColumnFactory';
import { datePresetFilter } from 'src/tables/TableV2/filters/filterFunctions';
import { ContentId } from 'src/utils/constants/contentId';
import { RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID } from 'src/utils/constants/contentIdMaps';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature } from 'src/WebApiController';

import { BuyerCreditCell } from '../components/BuyerCreditCell';
import { UnpaidSalesCell } from '../components/UnpaidSalesCell';
import {
  CardBrand,
  RetailCustomer,
  RetailTableColumnId,
} from '../RetailTable.types';

function getCardBrandLabel(brand: CardBrand): string {
  const labels: Record<CardBrand, string> = {
    visa: 'VISA',
    mastercard: 'MC',
    amex: 'AMEX',
    discover: 'DISC',
  };
  return labels[brand];
}

function getCardBrandColors(brand: CardBrand): { bg: string; text: string } {
  const brandColors: Record<CardBrand, { bg: string; text: string }> = {
    visa: { bg: '#1a1f71', text: '#ffffff' },
    mastercard: { bg: '#eb001b', text: '#ffffff' },
    amex: { bg: '#007bc1', text: '#ffffff' },
    discover: { bg: '#ff6600', text: '#ffffff' },
  };
  return brandColors[brand];
}

const columnHelper = createColumnHelper<RetailCustomer | null>();

export const RETAIL_TABLE_COLUMNS_CONFIG = {
  [RetailTableColumnId.Name]: createTextColumn<RetailCustomer | null>({
    id: RetailTableColumnId.Name,
    headerContentId:
      RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.Name],
    accessor: (data) => data?.name,
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    enableColumnFilter: true,
    meta: { columnFilterVariant: 'multiSelect' },
    customCellRenderer: ({
      value,
    }: {
      value: string | number | boolean | null | undefined;
    }) => (
      <BaseCell
        align="left"
        style={{ fontWeight: 600, color: vars.color.textStrong }}
      >
        {String(value)}
      </BaseCell>
    ),
  }),

  [RetailTableColumnId.Email]: createTextColumn<RetailCustomer | null>({
    id: RetailTableColumnId.Email,
    headerContentId:
      RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.Email],
    accessor: (data) => data?.email,
    size: 200,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    enableColumnFilter: true,
    meta: { columnFilterVariant: 'multiSelect' },
  }),

  [RetailTableColumnId.Phone]: createTextColumn<RetailCustomer | null>({
    id: RetailTableColumnId.Phone,
    headerContentId:
      RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.Phone],
    accessor: (data) => data?.phone,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    enableColumnFilter: true,
    meta: { columnFilterVariant: 'multiSelect' },
  }),

  [RetailTableColumnId.DefaultPayment]: columnHelper.accessor(
    (data) => data?.defaultPayment,
    {
      id: RetailTableColumnId.DefaultPayment,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      header: () => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[
                RetailTableColumnId.DefaultPayment
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: function Cell({ getValue, row: { original } }) {
        const hasCleanPaymentDisplay = useUserHasFeature(
          Feature.CleanPaymentDisplay
        );
        const value = getValue<RetailCustomer['defaultPayment']>();
        if (!original) {
          return <TableShimmeringDiv />;
        }
        if (!value) {
          return <BaseCell />;
        }
        const { bg, text } = getCardBrandColors(value.brand);
        return (
          <TableCellDiv align="left" showTooltip={false}>
            <Stack alignItems="center" gap="s">
              <span
                style={{
                  backgroundColor: bg,
                  color: text,
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 5px',
                  borderRadius: vars.borderRadius.s,
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                }}
              >
                {getCardBrandLabel(value.brand)}
              </span>
              <span
                style={{
                  color: vars.color.textPrimary,
                  fontSize: vars.typography.fontSize.sm,
                }}
              >
                {hasCleanPaymentDisplay
                  ? value.lastFour
                  : `•••• ${value.lastFour}`}
              </span>
            </Stack>
          </TableCellDiv>
        );
      },
    }
  ),

  [RetailTableColumnId.UnpaidBalance]: createMoneyColumn<RetailCustomer | null>(
    {
      id: RetailTableColumnId.UnpaidBalance,
      headerContentId:
        RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.UnpaidBalance],
      accessor: (data) =>
        data
          ? { amt: data.unpaidBalance, currency: 'USD', dec: 2, disp: null }
          : null,
      size: COLUMN_DEFAULT_SIZE_LG,
    }
  ),

  [RetailTableColumnId.Credit]: columnHelper.accessor(
    (data) => data?.creditBalance,
    {
      id: RetailTableColumnId.Credit,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      header: () => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.Credit]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row: { original } }) => {
        if (!original) {
          return <TableShimmeringDiv />;
        }
        return (
          <BuyerCreditCell
            balance={original.creditBalance}
            currencyCode={original.creditCurrencyCode}
          />
        );
      },
    }
  ),

  [RetailTableColumnId.UnpaidInvoices]: columnHelper.accessor(
    (data) => data?.salesGroupIds,
    {
      id: RetailTableColumnId.UnpaidInvoices,
      size: COLUMN_DEFAULT_SIZE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      header: () => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[
                RetailTableColumnId.UnpaidInvoices
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: function Cell({ getValue, row: { original } }) {
        const { setModal } = useContext(ModalContext);
        const value = getValue<RetailCustomer['salesGroupIds']>();
        if (!original) {
          return <TableShimmeringDiv />;
        }
        if (!value || value.length === 0) {
          return <BaseCell />;
        }

        const openSalesGroup = (salesGroupId: string) => {
          setModal(getSalesGroupModalConfigWithDeepLink(salesGroupId));
        };

        return (
          <UnpaidSalesCell
            salesGroupIds={value}
            onOpenSalesGroup={openSalesGroup}
          />
        );
      },
    }
  ),

  [RetailTableColumnId.BillingAddress]: createTextColumn<RetailCustomer | null>(
    {
      id: RetailTableColumnId.BillingAddress,
      headerContentId:
        RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[
          RetailTableColumnId.BillingAddress
        ],
      accessor: (data) => data?.billingAddress,
      size: 200,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      enableColumnFilter: true,
      meta: { columnFilterVariant: 'multiSelect' },
    }
  ),

  [RetailTableColumnId.ShippingAddress]:
    createTextColumn<RetailCustomer | null>({
      id: RetailTableColumnId.ShippingAddress,
      headerContentId:
        RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[
          RetailTableColumnId.ShippingAddress
        ],
      accessor: (data) => data?.shippingAddress,
      size: 200,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      enableColumnFilter: true,
      meta: { columnFilterVariant: 'multiSelect' },
    }),

  [RetailTableColumnId.LastActivity]: {
    ...createDateColumn<RetailCustomer | null>({
      id: RetailTableColumnId.LastActivity,
      headerContentId:
        RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.LastActivity],
      accessor: (data) => data?.lastActivity,
      size: COLUMN_DEFAULT_SIZE_LG,
      sectionType: SectionType.Retail,
      filterFn: datePresetFilter,
    }),
    meta: { columnFilterVariant: 'datePreset' as const },
  },

  [RetailTableColumnId.Notes]: createTextColumn<RetailCustomer | null>({
    id: RetailTableColumnId.Notes,
    headerContentId:
      RETAIL_TABLE_COLUMN_ID_TO_CONTENT_ID[RetailTableColumnId.Notes],
    accessor: (data) => data?.notes,
    size: 200,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    enableColumnFilter: true,
    meta: { columnFilterVariant: 'multiSelect' },
    customCellRenderer: ({
      value,
    }: {
      value: string | number | boolean | null | undefined;
    }) => (
      <BaseCell
        align="left"
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '200px',
        }}
      >
        {value ? String(value) : '—'}
      </BaseCell>
    ),
  }),

  [RetailTableColumnId.Actions]: columnHelper.display({
    id: RetailTableColumnId.Actions,
    size: 60,
    minSize: 60,
    maxSize: 60,
    header: () => <ColumnPicker tableColumnsSectionType={SectionType.Retail} />,
    enableSorting: false,
    enableColumnFilter: false,
    cell: function Cell({ row: { original }, table }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const onCustomerClick = (
        table.options.meta as { onCustomerClick?: (email: string) => void }
      )?.onCustomerClick;
      return (
        <TableCellDiv align="right" showTooltip={false}>
          <Stack direction="row" alignItems="center" gap="m">
            <span style={{ marginRight: vars.spacing.xxs }}>
              <IconButton
                icon={
                  <OpenLinkIcon
                    size={vars.iconSize.m}
                    withHoverEffect
                    fill={vars.color.textBrand}
                  />
                }
                titleContentId={ContentId.ViewDetails}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCustomerClick && original.email) {
                    onCustomerClick(original.email);
                  }
                }}
              />
            </span>
          </Stack>
        </TableCellDiv>
      );
    },
  }),
} as const;
